import { Controller } from "@hotwired/stimulus"

// The filter and More sheets. They slide up from the bottom, and close with the backdrop, Escape, or a
// drag down from the grabber or title row (data-sheet-handle), as on a phone.
const CLOSE_DISTANCE = 96
const CLOSE_VELOCITY = 0.5 // px per ms

export default class extends Controller {
  static targets = ["filterSheet", "filterBackdrop", "moreSheet", "moreBackdrop"]

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.endDrag()
    document.body.style.overflow = ""
  }

  openFilter(event) {
    event?.preventDefault()
    this.openSheet("filter")
  }

  openMore(event) {
    event?.preventDefault()
    this.openSheet("more")
  }

  openSheet(name) {
    // Close any open sheets first
    this.closeAll()

    const sheetTarget = `${name}SheetTarget`
    const backdropTarget = `${name}BackdropTarget`
    const hasSheet = `has${name.charAt(0).toUpperCase() + name.slice(1)}SheetTarget`

    if (this[hasSheet]) {
      this[sheetTarget].classList.add("open")
      this[backdropTarget].classList.add("open")
      document.body.style.overflow = "hidden"
      this.currentSheet = name
    }
  }

  close(event) {
    event?.preventDefault()
    this.closeAll()
  }

  closeAll() {
    if (this.hasFilterSheetTarget) {
      this.filterSheetTarget.classList.remove("open")
      this.filterBackdropTarget.classList.remove("open")
    }
    if (this.hasMoreSheetTarget) {
      this.moreSheetTarget.classList.remove("open")
      this.moreBackdropTarget.classList.remove("open")
    }
    document.body.style.overflow = ""
    this.currentSheet = null
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.closeAll()
    }
  }

  backdropClick(event) {
    if (event.target.classList.contains("sheet-backdrop")) {
      this.closeAll()
    }
  }

  // Drag to dismiss: the sheet follows the finger down (and resists being pulled up), the backdrop
  // lightens with it, and letting go far enough or fast enough closes it; otherwise it springs back
  startDrag(event) {
    if (!this.currentSheet || event.button > 0 || event.target.closest("a, button:not([data-sheet-handle])")) return

    this.drag = {
      sheet: this[`${this.currentSheet}SheetTarget`],
      backdrop: this[`${this.currentSheet}BackdropTarget`],
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      offset: 0
    }
    this.drag.sheet.classList.add("is-dragging")
    this.drag.backdrop.style.transition = "none"
    window.addEventListener("pointermove", this.onPointerMove)
    window.addEventListener("pointerup", this.onPointerUp)
    window.addEventListener("pointercancel", this.onPointerUp)
  }

  onPointerMove(event) {
    const drag = this.drag
    if (!drag) return

    const delta = event.clientY - drag.startY
    const elapsed = Math.max(1, event.timeStamp - drag.lastTime)
    drag.velocity = (event.clientY - drag.lastY) / elapsed
    drag.lastY = event.clientY
    drag.lastTime = event.timeStamp
    drag.offset = delta > 0 ? delta : -Math.sqrt(-delta) * 2

    drag.sheet.style.setProperty("--sheet-drag", `${drag.offset}px`)
    const height = drag.sheet.offsetHeight || 1
    drag.backdrop.style.opacity = String(Math.max(0, 1 - Math.max(0, drag.offset) / height))
  }

  onPointerUp() {
    const drag = this.drag
    if (!drag) return

    const dismiss = drag.offset > CLOSE_DISTANCE || (drag.offset > 16 && drag.velocity > CLOSE_VELOCITY)
    this.endDrag()
    if (dismiss) this.closeAll()
  }

  endDrag() {
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.onPointerUp)
    window.removeEventListener("pointercancel", this.onPointerUp)
    if (!this.drag) return

    const { sheet, backdrop } = this.drag
    sheet.classList.remove("is-dragging")
    sheet.style.removeProperty("--sheet-drag")
    backdrop.style.removeProperty("transition")
    backdrop.style.removeProperty("opacity")
    this.drag = null
  }
}
